import { minorUnitScale } from '@klopotun/domain';
import type { GrownCategory, MonthInsight } from '../../../entities/summary/index.js';
import { formatMonthName } from '../../../features/payment-view/index.js';
import {
  formatCompactAmount,
  formatMoneyMaybeApproximate,
  formatMonthTitle,
  formatShare,
} from '../../../features/summary-view/index.js';

const NON_BREAKING_SPACE = ' ';

const ROUGHLY = '~';

const PERCENT = 100;

const knownUsualOf = (insight: MonthInsight): number | null => {
  const hasUsual = insight.usualMinor !== null && insight.usualMinor > 0;

  return hasUsual ? insight.usualMinor : null;
};

const roughAmountOf = (amountMinor: number, currency: string): string =>
  `${ROUGHLY}${formatCompactAmount(amountMinor, currency)}${NON_BREAKING_SPACE}${currency}`;

const spentOf = (insight: MonthInsight): string =>
  formatMoneyMaybeApproximate(insight.spentMinor, insight.baseCurrency, insight.isSpentApproximate);

const comparisonOf = (forecastMinor: number, usualMinor: number): string => {
  const percent = Math.round(((forecastMinor - usualMinor) * PERCENT) / usualMinor);

  if (percent === 0) {
    return 'як зазвичай';
  }

  const share = `на${NON_BREAKING_SPACE}${formatShare(Math.abs(percent) / PERCENT)}`;

  return percent > 0 ? `${share} більше звичного` : `${share} менше звичного`;
};

export const isInsightEmpty = (insight: MonthInsight): boolean =>
  insight.spentMinor === 0 && insight.forecastMinor === 0;

export const monthHeadlineOf = (insight: MonthInsight): string =>
  `Уже витрачено ${spentOf(insight)}`;

export const monthLineTitleOf = (insight: MonthInsight): string =>
  `${formatMonthTitle(formatMonthName(insight.month))}: уже витрачено ${spentOf(insight)}`;

export const usualLineOf = (insight: MonthInsight): string | null => {
  const usualMinor = knownUsualOf(insight);

  if (usualMinor === null) {
    return null;
  }

  return `Зазвичай за місяць ${roughAmountOf(usualMinor, insight.baseCurrency)}`;
};

export const forecastLineOf = (insight: MonthInsight): string | null => {
  const isNothingLeftToCome =
    insight.daysLeft === 0 && insight.forecastMinor === insight.spentMinor;

  if (isNothingLeftToCome || !insight.isHistoryComplete) {
    return null;
  }

  const hasPaceEstimate = insight.daysLeft > 0;
  const forecastAmount = formatMoneyMaybeApproximate(
    insight.forecastMinor,
    insight.baseCurrency,
    hasPaceEstimate || insight.isForecastApproximate,
  );
  const forecast = `На кінець місяця ${forecastAmount}`;
  const usualMinor = knownUsualOf(insight);

  if (usualMinor === null) {
    return forecast;
  }

  return `${forecast} — ${comparisonOf(insight.forecastMinor, usualMinor)}`;
};

const categoryTitleOfGrown = (row: GrownCategory): string =>
  row.emoji === null ? row.name : `${row.emoji} ${row.name}`;

export const grownTitleOf = (row: GrownCategory, currency: string): string => {
  const title = categoryTitleOfGrown(row);
  const spent = formatCompactAmount(row.spentMinor, currency);
  const isUsuallyNothing = Math.round(row.usualMinor / minorUnitScale(currency)) === 0;

  if (isUsuallyNothing) {
    return `${title}: ${spent}, зазвичай${NON_BREAKING_SPACE}майже${NON_BREAKING_SPACE}нічого`;
  }

  const usual = formatCompactAmount(row.usualMinor, currency);

  return `${title}: ${spent} замість${NON_BREAKING_SPACE}звичних${NON_BREAKING_SPACE}${usual}`;
};
